/**
 * Hook that provides an instantly-ready PaymentRequest for Apple Pay / Google Pay
 * 
 * Uses the global Stripe preloader to provide:
 * 1. Cached Stripe instance (no network call on repeat visits)
 * 2. Pre-checked canMakePayment result
 * 3. PaymentRequest that only needs amount updates
 */

import { useState, useEffect, useRef, useCallback } from 'react';
import { useCurrencyDetection } from '@/hooks/use-currency-detection';
import { useAppConfig } from '@/hooks/use-app-config';
import {
  getStripeInstance,
  getStripeReactModule,
  getCachedPublishableKey,
  getPreloadedPaymentResult,
  createPaymentRequest,
} from '@/lib/stripePreloader';
import type { Stripe, PaymentRequest } from '@stripe/stripe-js';

interface UseReadyPaymentRequestOptions {
  defaultTier?: 'weekly' | 'monthly' | 'annual';
  enabled?: boolean;
}

interface UseReadyPaymentRequestReturn {
  paymentRequest: PaymentRequest | null;
  canMakePayment: boolean;
  isReady: boolean;
  stripe: Stripe | null;
  publishableKey: string | null;
  Elements: React.ComponentType<any> | null;
  PaymentRequestButtonElement: React.ComponentType<any> | null;
  updateAmount: (tier: 'weekly' | 'monthly' | 'annual') => void;
}

export function useReadyPaymentRequest(
  options: UseReadyPaymentRequestOptions = {}
): UseReadyPaymentRequestReturn {
  const { defaultTier = 'annual', enabled = true } = options;
  const { currency, country } = useCurrencyDetection();
  const { data: appConfig } = useAppConfig();

  const [paymentRequest, setPaymentRequest] = useState<PaymentRequest | null>(null);
  const [canMakePayment, setCanMakePayment] = useState(false);
  const [isReady, setIsReady] = useState(false);
  const [stripe, setStripe] = useState<Stripe | null>(null);
  const [publishableKey, setPublishableKey] = useState<string | null>(null);
  const [Elements, setElements] = useState<React.ComponentType<any> | null>(null);
  const [PaymentRequestButtonElement, setPaymentRequestButtonElement] = 
    useState<React.ComponentType<any> | null>(null);

  const initStartedRef = useRef(false);
  const currentPrRef = useRef<PaymentRequest | null>(null);

  // Prices
  const weeklyPrice = appConfig?.weekly_price || 3.99;
  const monthlyPrice = appConfig?.monthly_price || 7.99;
  const annualPrice = appConfig?.annual_price || 59.99;

  // Initialize - uses preloaded Stripe for instant availability
  useEffect(() => {
    if (!enabled || initStartedRef.current) return;
    initStartedRef.current = true;

    const init = async () => {
      console.log('[useReadyPaymentRequest] Initializing...');
      const startTime = performance.now();

      try {
        // Load React components first (usually cached from preload)
        const reactModule = await getStripeReactModule();
        setElements(() => reactModule.Elements);
        setPaymentRequestButtonElement(() => reactModule.PaymentRequestButtonElement);

        // Check if preloader already has everything ready
        const preloaded = getPreloadedPaymentResult();
        const cachedKey = getCachedPublishableKey();

        if (cachedKey) {
          setPublishableKey(cachedKey);
        }

        // Get Stripe instance (usually instant from cache)
        const stripeInstance = await getStripeInstance();
        if (!stripeInstance) {
          console.warn('[useReadyPaymentRequest] No Stripe instance');
          setIsReady(true);
          return;
        }

        setStripe(stripeInstance);
        
        // Update publishable key if we got it from the instance creation
        const key = getCachedPublishableKey();
        if (key) setPublishableKey(key);

        // If preloader has a ready PaymentRequest, we can use canMakePayment result
        // but we need to create a fresh PR with correct currency/amount
        const price = defaultTier === 'monthly' ? monthlyPrice : annualPrice;
        const amount = Math.round(price * 100);

        const { paymentRequest: pr, canMakePayment: canPay } = await createPaymentRequest({
          country: country || 'US',
          currency: currency.code.toLowerCase(),
          amount,
          label: defaultTier === 'monthly' ? 'Monthly Subscription' : 'Annual Subscription',
        });

        if (pr && canPay) {
          setPaymentRequest(pr);
          setCanMakePayment(true);
          currentPrRef.current = pr;
        }

        const elapsed = Math.round(performance.now() - startTime);
        console.log(`[useReadyPaymentRequest] Ready in ${elapsed}ms, canPay:`, !!canPay);
      } catch (e) {
        console.error('[useReadyPaymentRequest] Init error:', e);
      } finally {
        setIsReady(true);
      }
    };

    init();
  }, [enabled, country, currency.code, defaultTier, monthlyPrice, annualPrice]);

  // Update amount when tier changes
  const updateAmount = useCallback((tier: 'weekly' | 'monthly' | 'annual') => {
    const pr = currentPrRef.current;
    if (!pr) return;

    const price = tier === 'weekly' ? weeklyPrice : tier === 'monthly' ? monthlyPrice : annualPrice;
    const amount = Math.round(price * 100);
    const label = tier === 'weekly' ? 'Weekly Subscription' : tier === 'monthly' ? 'Monthly Subscription' : 'Annual Subscription';

    console.log('[useReadyPaymentRequest] Updating amount for tier:', tier, 'amount:', amount);

    pr.update({
      total: {
        label,
        amount,
      },
    });
  }, [weeklyPrice, monthlyPrice, annualPrice]);

  return {
    paymentRequest,
    canMakePayment,
    isReady,
    stripe,
    publishableKey,
    Elements,
    PaymentRequestButtonElement,
    updateAmount,
  };
}
